import React from 'react'
import { cn } from 'src/enviroment/lib/utils'

export type TypographyVariant =
  | '4xl'
  | '4xl-bold'
  | '3xl-bold'
  | '2xl-semibold'
  | '2xl-bold'
  | '2xl'
  | 'xl-bold'
  | 'xl'
  | 'lg'
  | 'lg-bold'
  | 'lg-medium'
  | 'base-bold'
  | 'base-semibold'
  | 'base'
  | 'base-medium'
  | 'sm-semibold'
  | 'sm-bold'
  | 'sm-medium'
  | 'sm'
  | 'xs'
  | 'xs-medium'

type TypographyElement = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p' | 'span' | 'div' | 'label'

interface TypographyVariantConfig {
  family: string
  size: string
  weight: string
  element: TypographyElement
}

interface TypographyProps extends React.HTMLAttributes<HTMLElement> {
  variant: TypographyVariant
  element?: TypographyElement
  className?: string
  children: React.ReactNode
}

const TYPOGRAPHY_VARIANTS: Record<TypographyVariant, TypographyVariantConfig> = {
  '4xl': {
    family: 'font-inter',
    size: 'text-4xl',
    weight: 'font-normal',
    element: 'h1',
  },
  '4xl-bold': {
    family: 'font-inter',
    size: 'text-[36px]',
    weight: 'font-bold',
    element: 'h1',
  },
  '3xl-bold': {
    family: 'font-inter',
    size: 'text-[30px]',
    weight: 'font-bold',
    element: 'h1',
  },
  '2xl-bold': {
    family: 'font-inter',
    size: 'text-[24px]',
    weight: 'font-bold',
    element: 'h1',
  },
  '2xl': {
    family: 'font-inter',
    size: 'text-2xl',
    weight: 'font-normal',
    element: 'h1',
  },
  '2xl-semibold': {
    family: 'font-inter',
    size: 'text-2xl',
    weight: 'font-semibold',
    element: 'h1',
  },
  'xl-bold': {
    family: 'font-inter',
    size: 'text-xl',
    weight: 'font-bold',
    element: 'h2',
  },
  xl: {
    family: 'font-inter',
    size: 'text-xl',
    weight: 'font-normal',
    element: 'h2',
  },
  'lg-bold': {
    family: 'font-inter',
    size: 'text-lg',
    weight: 'font-bold',
    element: 'h3',
  },
  lg: {
    family: 'font-inter',
    size: 'text-lg',
    weight: 'font-normal',
    element: 'h3',
  },
  'lg-medium': {
    family: 'font-inter',
    size: 'text-[18px]',
    weight: 'font-medium',
    element: 'h4',
  },
  'base-bold': {
    family: 'font-inter',
    size: 'text-base',
    weight: 'font-bold',
    element: 'h4',
  },
  'base-semibold': {
    family: 'font-inter',
    size: 'text-base',
    weight: 'font-semibold',
    element: 'div',
  },
  'base-medium': {
    family: 'font-inter',
    size: 'text-[16px]',
    weight: 'font-medium',
    element: 'div',
  },
  base: {
    family: 'font-inter',
    size: 'text-base',
    weight: 'font-normal',
    element: 'div',
  },
  'sm-semibold': {
    family: 'font-inter',
    size: 'text-sm',
    weight: 'font-semibold',
    element: 'div',
  },
  'sm-medium': {
    family: 'font-inter',
    size: 'text-sm',
    weight: 'font-medium',
    element: 'div',
  },
  sm: {
    family: 'font-inter',
    size: 'text-sm',
    weight: 'font-normal',
    element: 'div',
  },
  'sm-bold': {
    family: 'font-inter',
    size: 'text-[14px]',
    weight: 'font-bold',
    element: 'span',
  },
  xs: {
    family: 'font-inter',
    size: 'text-xs',
    weight: 'font-normal',
    element: 'span',
  },
  'xs-medium': {
    family: 'font-inter',
    size: 'text-xs',
    weight: 'font-medium',
    element: 'span',
  },
}

const Typography = ({ variant, element, className, children, ...props }: TypographyProps) => {
  const config = TYPOGRAPHY_VARIANTS[variant]

  // If element is explicitly passed, use it. Otherwise check if we need to prevent invalid nesting
  const Component = element || (props['aria-label'] || props.role === 'paragraph' ? 'p' : config.element)

  const variantClasses = [config.family, config.size, config.weight].join(' ')

  return (
    <Component className={cn(variantClasses, className)} {...props}>
      {children}
    </Component>
  )
}

export default Typography
